import {DatePipe} from '@angular/common'
import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core'
import {Router} from '@angular/router'
import {AuthService, CmsComponentConnector, CmsLinkComponent, RoutingService} from '@spartacus/core'
import {CurrentProductService} from '@spartacus/storefront'
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs'
import {distinctUntilChanged, filter, map, switchMap, take, tap} from 'rxjs/operators'
import {EbAnalyticsService} from 'src/app/features/analytics/services/facades/eb-analytics.service'
import {EbAuthStatePersistenceService} from 'src/app/features/auth/services/eb-auth-state-persistence.service'
import {EbAuthService} from 'src/app/features/auth/services/eb-auth.service'
import {EbFavorites, EbGroupedFavorites} from '../../../favorites/interfaces/eb-favorites.interface'
import {EbFavoritesService} from '../../../favorites/services/facades/eb-favorites.service'
import {EbPageMetaLinkService} from '../../../seo/eb-page-meta-link.service'
import {EbCountryService} from "../../../site-context/services/eb-country.service";
import {EbLanguageService} from '../../../site-context/services/eb-language.service'
import {EbAvailability} from '../../interfaces/eb-availability.interface'
import {
  EBDocument,
  EbMediaTypeEnum,
  EbProduct,
  EbProductPrice,
  EbProductTypeEnum
} from '../../interfaces/eb-product.interface'
import {EbAvailabilityService} from '../../services/facades/eb-availability.service'
import {EbPricesService} from '../../services/facades/eb-prices.service'

@Component({
    selector: 'eb-product-detail',
    templateUrl: './eb-product-detail.component.html',
    styleUrls: ['./eb-product-detail.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class EbProductDetailComponent implements OnInit, OnDestroy {
  product$: Observable<EbProduct> = this.currentProductService.getProduct() as Observable<EbProduct>
  price: EbProductPrice
  isFavorite$: Observable<boolean>
  favoritesList$: Observable<EbFavorites[]>
  favoritesListLoading$: Observable<boolean>
  groupedFavoritesList$: Observable<EbGroupedFavorites>
  isDisabled = false
  priceLoading :boolean
  private readonly subscription = new Subscription()
  availability: EbAvailability
  availabilityError: boolean
  availabilityLoading: boolean
  isUserLoggedIn$: Observable<boolean> = this.userAuth.isUserLoggedIn()
  private readonly mode$ = new BehaviorSubject<string>(null)
  linkComponent$: Observable<CmsLinkComponent>
  localizedDateEmitter$ = new BehaviorSubject(false)
  localizedDate: string
  get EbProductTypeEnum() {
    return EbProductTypeEnum
  }

  get EbMediaTypeEnum() {
    return EbMediaTypeEnum
  }

  get availabilityItem() {
    return this.availability?.items?.[0]
  }

  constructor(
    protected currentProductService: CurrentProductService,
    protected pricesService: EbPricesService,
    protected availabilityService: EbAvailabilityService,
    private readonly cd: ChangeDetectorRef,
    protected userAuth: AuthService,
    protected favoritesService: EbFavoritesService,
    protected routingService: RoutingService,
    protected languageService: EbLanguageService,
    protected router: Router,
    protected ebPageMetaLinkService: EbPageMetaLinkService,
    protected authService: EbAuthService,
    protected cmsComponentConnector: CmsComponentConnector,
    @Inject(EbAuthStatePersistenceService)
    protected ebAuthStatePersistenceService: EbAuthStatePersistenceService,
    protected ebAnalyticsService: EbAnalyticsService,
    private readonly ebCountryService: EbCountryService
  ) {
    this.linkComponent$ = this.routingService
      .getRouterState()
      .pipe(
        switchMap(routerState =>
          this.cmsComponentConnector
            .getList(['ebRegisterLink'], routerState.state.context)
            .pipe(map(components => components[0]))
        )
      )
  }

  ngOnInit() {
    this.fetchPrice()
    this.fetchAvailability()
    this.fetchFavorites()
    this.localizeDate()
    this.subscription.add(
      combineLatest([
        this.languageService.getActive(),
        this.product$,
        this.routingService.getRouterState()
      ])
        .pipe(
          distinctUntilChanged((oldState, newStateL) => oldState[1] === newStateL[1]),
          map(([, product, routerState]) => {
            if (
              !routerState.state.url.includes(product?.fullProductPath) &&
              routerState.state.url.includes(product?.code)
            ) {
              this.router.navigate([
                `${product?.fullProductPath}/${
                  this.isVariant(product?.ebProductType) ? 'v' : 'p'
                }/${product?.code}`
              ])
            }
          })
        )
        .subscribe()
    )

    this.subscription.add(
      this.ebCountryService.countryChanged$.subscribe(() => {
        window.location.reload();
      })
    )
  }

  getDocumentByType(documents: EBDocument[], type: EbMediaTypeEnum): EBDocument {
    return documents?.find(value => value.ebMediaType === type)
  }

  private fetchFavorites() {
    this.favoritesListLoading$ = combineLatest([
      this.favoritesService.listLoading$,
      this.isUserLoggedIn$
    ]).pipe(map(([loading, loggedIn]) => loading && loggedIn))
    this.favoritesList$ = combineLatest([this.product$, this.isUserLoggedIn$]).pipe(
      switchMap(([product, loggedIn]) => {
        return loggedIn ? this.favoritesService.getLists(product.code) : of([])
      })
    )
    this.isFavorite$ = this.favoritesList$.pipe(
      map(favorites => !!favorites.find(favorite => favorite.containsProduct))
    )
    this.groupedFavoritesList$ = this.favoritesService.getGroupedLists()
  }

  toggleFavorite(event: MouseEvent) {
    combineLatest([this.isFavorite$, this.product$, this.favoritesList$])
      .pipe(take(1))
      .subscribe(([isFavorite, product, favoritesList]) => {
        if (!isFavorite) {
          if(product.orderable === true){
            this.ebAnalyticsService.addToFavorites(this.price?.items[0]?.itemNetPricePerUnit?.value.toString())
          } else {
            this.ebAnalyticsService.addToFavorites("0")
          }
          event.preventDefault()
          event.stopPropagation()
          this.favoritesService.addProductToFavorites(
            product.code,
            favoritesList.find(favorites => favorites.recommendationsList)
          )
        }
      })
  }

  handleQuantityChange(quantity = 1) {
    this.fetchAvailability(quantity)
    this.fetchPrice(quantity)
  }

  fetchPrice(quantity = 1) {
    this.subscription.add(
      this.product$
        .pipe(
          filter(product => this.isVariant(product?.ebProductType) && product.orderable),
          tap(() => {
            this.priceLoading = true
          }),
          switchMap(product => {
            return this.pricesService.fetchPrice({
              product: product.code,
              quantity
            })
          }),
          tap(() => (this.priceLoading = false))
        )
        .subscribe(price => {
          this.price = price
          this.pushMatomoEvents()
          this.cd.detectChanges()
        })
    )
  }

  fetchAvailability(quantity = 1) {
    this.subscription.add(
      this.product$
        .pipe(
          filter(product => this.isVariant(product?.ebProductType) && product.orderable),
          tap(() => {
            this.availabilityLoading = true
          }),
          switchMap(product => {
            return this.availabilityService.fetchAvailability({
              product: product.code,
              quantity
            })
          }),
          tap(() => (this.availabilityLoading = false))
        )
        .subscribe(availability => {
          if (availability.error || !availability?.items?.length) {
            this.availability = {}
            this.availabilityError = true
          } else {
            this.availability = availability
            this.availabilityError = false
          }
          this.cd.detectChanges()
        })
    )
  }

  isVariant(productType: EbProductTypeEnum): boolean {
    return productType === EbProductTypeEnum.VARIANT
  }

  hasPrice(product): boolean {
    if (product?.code === this.price?.items?.[0]?.product) {
      return this.price?.items?.[0]?.itemNetPricePerUnit && this.price?.priceTotalValue?.value > 0
    } else {
      return false
    }
  }

  hasScalePrice(product): boolean {
    if (product?.code === this.price?.items?.[0]?.product) {
      return this.price?.items?.[0]?.scales && this.price?.items?.[0]?.scales.length > 1
    } else {
      return false
    }
  }

  hasSuccessor(product: EbProduct): boolean {
    return product.successor !== undefined
  }

  loginWithSapIDP() {
    this.authService.loginWithSapIDP()
  }

  reportRenderMode(mode: string) {
    this.mode$.next(mode)
    return mode
  }
  localizeDate() {
    this.subscription.add(
      this.product$.subscribe(product => {
        this.languageService.getActive().subscribe(lang => {
          this.localizedDate = new DatePipe(lang).transform(product.expirationDate)
          this.localizedDateEmitter$.next(true)
        })
      })
    )
  }

  requestRfpEvent() {
    this.ebAnalyticsService.requestForQuotation("Variants Page")
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe()
    this.ebPageMetaLinkService.setAlternateLinks([])
  }

  /** Push track view events of variant product to Matomo **/
  private pushMatomoEvents() {
    this.product$.subscribe((product) => {
      if(product.code === this.price?.items[0]?.product) {
        this.ebAnalyticsService.pushViewVariantProductEvents(product, this.price?.items[0]?.itemNetPricePerUnit.value)
      }
    })
  }
}
